import { z } from "zod";

// TES-804 (Ö-180 ✅ 2026-09-18, Ö-182 ✅ 2026-09-23) — handing over who is
// responsible for a patient's care.
//
// WHY THIS EXISTS AT ALL. `resolveCareTeamDoctor` derives the responsible
// doctor from the most recent ACTIVE CareRelationship, and that relationship
// arises from the WORK: it is established when a doctor touches the patient.
// The file says so itself — "nobody can *assign* a responsible doctor today,
// because no surface does that". Emelie's answer to Ö-180 is that a derived
// owner is no longer enough: responsibility becomes an ASSIGNED fact with its
// own history.
//
// WHY A HISTORY AND NOT A COLUMN. `resolve-care-team-doctor.ts` warns against
// "inventing a second, parallel 'responsible doctor' column", which would
// leave "two places to disagree about the same fact". A handover LOG avoids
// that: the current owner is the latest row, so there is exactly one source,
// and the audit trail (who → who, when, why, by whom) falls out of the same
// rows rather than being maintained beside them.

// Ö-182, verbatim:
// "semester/annan frånvaro · rotation/kapacitetsfördelning · bytt vårdspår/vårdnivå · annan kompetens · second opinion · patientens önskemål · avslutad anställning/uppdrag · annat med obligatorisk fritext".
//
// Eight values, and `other` carries its own text — same shape as
// CARE_CASE_CHANGE_REASONS, which is the pattern this deliberately copies
// rather than inventing a second convention for structured reasons.
export const DOCTOR_HANDOVER_REASONS = [
  "leave_or_absence",
  "rotation_or_capacity",
  "changed_care_track",
  "different_expertise",
  "second_opinion",
  "patient_request",
  "employment_ended",
  "other",
] as const;
export const doctorHandoverReasonSchema = z.enum(DOCTOR_HANDOVER_REASONS);
export type DoctorHandoverReason = z.infer<typeof doctorHandoverReasonSchema>;

// The reason is INTERNAL. Ö-182:
// "Patienten ser ny ansvarig läkare, inte intern orsak."
// Nothing in this module may reach a patient DTO — the patient surface reads
// only the resolved name.
export const assignDoctorSchema = z
  .object({
    // The doctor taking over. Validated against a real, active doctor at the
    // service layer; a handover to someone who cannot act on it would leave
    // the patient owned by nobody, which is the one outcome Ö-180 forbids.
    doctorId: z.string().min(1),
    reasonCode: doctorHandoverReasonSchema,
    // Optional except for `other`, where the code carries nothing on its own.
    reasonText: z.string().trim().max(2000).optional(),
    // Ö-225 (Emelie 2026-09-23) —
    // "När det går ska ursprunglig läkare färdigställa och signera FÖRE åtkomsten avslutas. Vid abrupt frånvaro skapas ett prioriterat undantagsärende."
    //
    // So a handover away from a doctor who still owes a consult note is
    // refused by default, and this flag is the exception being taken
    // deliberately rather than by omission. Same shape as TES-736's
    // `withFollowUpRemaining`: the server refuses, the human confirms, the
    // audit row records that they did.
    //
    // It is NOT a way to skip the note. The note still has to be written —
    // by the successor, as a marked retrospective entry — and this only says
    // that waiting for the original doctor is no longer possible.
    acknowledgeUndocumented: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.reasonCode === "other" && !v.reasonText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reasonText"],
        message: "reason_text_required",
      });
    }
  });
export type AssignDoctorRequest = z.infer<typeof assignDoctorSchema>;

// One row of the handover history, as the admin reads it back. `fromDoctor`
// is null for the first assignment — there was no predecessor to name, and
// recording one that did not exist would make the trail claim something
// false.
export const doctorHandoverEntrySchema = z.object({
  id: z.string(),
  toDoctor: z.object({ id: z.string(), displayName: z.string() }),
  fromDoctor: z.object({ id: z.string(), displayName: z.string() }).nullable(),
  reasonCode: doctorHandoverReasonSchema,
  reasonText: z.string().nullable(),
  assignedBy: z.object({ id: z.string(), displayName: z.string() }).nullable(),
  assignedAt: z.string(),
});
export type DoctorHandoverEntry = z.infer<typeof doctorHandoverEntrySchema>;

export const doctorHandoverHistorySchema = z.object({
  // The current responsible doctor, or null when none has been ASSIGNED —
  // in which case the care-team header still falls back to the derivation.
  current: z.object({ id: z.string(), displayName: z.string() }).nullable(),
  entries: z.array(doctorHandoverEntrySchema),
});
export type DoctorHandoverHistory = z.infer<typeof doctorHandoverHistorySchema>;

// Ö-180's second rule:
// "Öppna läkarärenden och bevakningar ska antingen flyttas till den nya ansvariga läkaren, eller behålla en uttryckligen namngiven annan ägare."
//
// So a handover has side effects, and they must not be silent — a doctor who
// hands a patient over is entitled to know that three booked consults moved
// with her. That is what `moved` carries.
//
// What is NOT in here is as deliberate as what is. A consult that has already
// happened but is not yet documented stays with the doctor who held it: he
// was in the room, and he is the only one who can document it truthfully.
// Moving it would make the new doctor the author of a consult she never
// attended — which is the "behåller sin faktiska avsändare" half of the same
// decision, and it leaves a named owner either way.
export const doctorHandoverResultSchema = doctorHandoverHistorySchema.extend({
  moved: z.object({
    // Booked consults that had not yet started, and were on the outgoing
    // doctor. A consult booked with an explicitly named stand-in (Ö-184)
    // is left alone: it already has the owner someone chose.
    meetings: z.number().int().nonnegative(),
  }),
});
export type DoctorHandoverResult = z.infer<typeof doctorHandoverResultSchema>;
